import { DI } from '@intigral/constant/config/DIContainer';
import { IAmazonActivateUseCase } from '../../core/usecases/slp/AmazonActivateUseCase';
import { IBundleAndSaveUseCase } from '../../core/usecases/slp/BundleAndSaveUseCase';
import { IPurchaseAddonUseCase } from '../../core/usecases/slp/PurchaseAddonUseCase';
import { container } from '../../DataDomain';
import {
    IItemsToPurchase,
    IAddonPurchaseResponse,
    IAmazonActivateResponse,
    IBundleAndSaveParams,
    IBundleAndSaveResponse,
    IUserDetailsEntity,
    ISubscriptionRevionResponse,
    IBundleAndSaveInfoParams,
    IBundleAndSaveInfoResponse,
    IBundleAndSaveRulesParams,
    IBundleAndSaveRulesResponse,
} from '../../entity';
import { getBucketIdFromAccountId } from '@intigral/utils';
import { IGetUserRevisionUseCase } from '../../core/usecases/cms/GetUserRevisionUseCase';
// import { IGetSubscriptionStatusUseCase } from '../../core/usecases/subscription/GetSubscriptionStatusUseCase';
import { ISubscriptionStatus } from '../../entity/interface/iSubscriptionStatus';
import { IGetSubscriptionStatusUseCase } from '../../core/usecases/UserIdentity/GetSubscriptionStatusUseCase';
import { IEntitlementService } from './EntitlementService';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';
import { IBundleAndSaveRulesUseCase } from '../../core/usecases/slp/BundleAndSaveRulesUseCase';
import { IBundleAndSaveInfoUseCase } from '../../core/usecases/slp/BundleAndSaveInfoUseCase';
// import { subscriptionStatusMock } from '../../subscriptionStatusMock';
export interface ISlpService {
    purchaseAddon(
        username: string,
        itemsToPurchase: IItemsToPurchase[],
        pin: string,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IAddonPurchaseResponse>;

    amazonActivate(entitlementId: string): Promise<IAmazonActivateResponse>;

    updateUserIfSubscriptionChanged(
        currentUser: IUserDetailsEntity,
        shouldCallRevisionApi: boolean,
        geoLocation?: string,
        shouldCallSubsTierApi?: boolean
    ): Promise<ISubscriptionRevionResponse | null>;

    bundleAndSave(payload: IBundleAndSaveParams): Promise<IBundleAndSaveResponse>;
    bundleAndSaveInfo(payload: IBundleAndSaveInfoParams, identityToken?: string): Promise<IBundleAndSaveInfoResponse>;
    bundleAndSaveRules(payload: IBundleAndSaveRulesParams, identityToken?: string): Promise<IBundleAndSaveRulesResponse>;
}

export class SlpService implements ISlpService {
    // Lazy getters for use cases to avoid circular dependencies

    private get formSignatureObjectUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }

    private get purchaseAddonUseCase(): IPurchaseAddonUseCase {
        return container.resolve<IPurchaseAddonUseCase>(DI.USECASES.IPurchaseAddonUseCase);
    }

    private get amazonActivateUseCase(): IAmazonActivateUseCase {
        return container.resolve<IAmazonActivateUseCase>(DI.USECASES.IAmazonActivateUseCase);
    }

    private get bundleAndSaveUseCase(): IBundleAndSaveUseCase {
        return container.resolve<IBundleAndSaveUseCase>(DI.USECASES.IBundleAndSaveUseCase);
    }

    private get bundleAndSaveInfoUseCase(): IBundleAndSaveInfoUseCase {
        return container.resolve<IBundleAndSaveInfoUseCase>(DI.USECASES.IBundleAndSaveInfoUseCase);
    }
    private get bundleAndSaveRulesUseCase(): IBundleAndSaveRulesUseCase {
        return container.resolve<IBundleAndSaveRulesUseCase>(DI.USECASES.IBundleAndSaveRulesUseCase);
    }

    private get getUserRevisionUseCase(): IGetUserRevisionUseCase {
        return container.resolve<IGetUserRevisionUseCase>(DI.USECASES.IGetUserRevisionUseCase);
    }

    private get getSubscriptionStatusUseCase(): IGetSubscriptionStatusUseCase {
        return container.resolve<IGetSubscriptionStatusUseCase>(DI.USECASES.IGetSubscriptionStatusUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    private async addUserWithSubscriptions(user: IUserDetailsEntity): Promise<IUserDetailsEntity> {
        if (!user.username || !user.apiKey || !user.authToken || !user.identityToken) {
            return user;
        }
        const subscriptions: ISubscriptionStatus = await this.getSubscriptionStatusUseCase.execute(
            user?.apiKey,
            user?.username,
            user?.authToken,
            user?.identityToken
        );

        // mock data should remove start

        // const subscriptions: ISubscriptionStatus = subscriptionStatusMock?.data;

        // mock data should remove end

        if (subscriptions?.identityToken) {
            user.identityToken = subscriptions.identityToken;
        }
        if (!user.email && subscriptions?.tierStatus?.email) {
            user.email = subscriptions.tierStatus.email;
        }

        user.subscriptionGUID = Array.isArray(subscriptions?.subList)
            ? subscriptions.subList?.filter((item) => item?.active === true).map((sub) => sub.subscriptionGuid)
            : [];
        user.subscriptionRev = subscriptions?.subRev;
        user.subscriptionStatus = subscriptions?.subList;
        user.tierConfig = subscriptions?.tier;
        user.tierStatus = subscriptions?.tierStatus;
        user.netflixSubStatus = subscriptions?.netflixSubStatus || null;
        user.amazonSubStatus = subscriptions?.amazonSubStatus;
        user.disneySubStatus = subscriptions?.disneySubStatus;
        user.ppvList = subscriptions?.ppvList;
        if (subscriptions?.userOperator) {
            user.operator = subscriptions.userOperator;
        }
        return user;
    }



    async purchaseAddon(
        username: string,
        itemsToPurchase: IItemsToPurchase[],
        pin: string,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IAddonPurchaseResponse> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.purchaseAddonUseCase.execute(
            username,
            itemsToPurchase,
            pin,
            authToken,
            authSign,
            identityToken,
            csrfToken
        );
    }

    async updateUserIfSubscriptionChanged(
        currentUser: IUserDetailsEntity,
        isRevisionApiCall = true,
        geoLocation = '',
        shouldCallSubsTierApi = false
    ): Promise<ISubscriptionRevionResponse | any> {
        if (isRevisionApiCall) {
            const bucketId = getBucketIdFromAccountId(currentUser.accountId);
            const result = await this.getUserRevisionUseCase.execute(bucketId);
            const revisionData = result[currentUser.accountId];
            const userData = { ...currentUser };
            userData.flRev = revisionData?.flRev;
            userData.consentRev = revisionData?.consentRev;
            userData.prev = revisionData?.prev;

            // // mock data should remove start
            // if (isRevisionApiCall) {
            //     const user: any = await this.addUserWithSubscriptions(currentUser);
            // const subscriptionTiersRes: any = this.getSubscriptionTierApi(currentUser, geoLocation, shouldCallSubsTierApi);
            //     return { user: { ...userData, ...user, subscriptionRev: user?.subscriptionRev }, subscriptionTiers: subscriptionTiersRes };
            // }
            // // mock data should remove end

            if (currentUser?.subscriptionRev) {
                if (revisionData?.subRev > currentUser?.subscriptionRev) {
                    const user: any = await this.addUserWithSubscriptions(currentUser);
                    const subscriptionTiersRes: any = this.getSubscriptionTierApi(
                        user,
                        geoLocation,
                        shouldCallSubsTierApi
                    );
                    return {
                        user: { ...userData, ...user, subscriptionRev: user?.subscriptionRev },
                        subscriptionTiers: subscriptionTiersRes,
                    };
                }
            } else if (
                !currentUser?.flRev ||
                !currentUser?.consentRev ||
                !currentUser?.prev ||
                revisionData?.flRev > currentUser?.flRev ||
                revisionData?.consentRev > currentUser?.consentRev ||
                revisionData?.prev > currentUser?.prev
            ) {
                return { user: { ...userData } };
            }
        } else {
            const subscriptionTiersRes: any = this.getSubscriptionTierApi(
                currentUser,
                geoLocation,
                shouldCallSubsTierApi
            );
            const user: any = await this.addUserWithSubscriptions(currentUser);
            return { user: { ...user }, subscriptionTiers: subscriptionTiersRes };
        }
    }

    private getSubscriptionTierApi = async (
        user: IUserDetailsEntity,
        geoLocation: string,
        shouldCallSubsTierApi: boolean
    ) => {
        let subscriptionTiersRes = null;
        if (shouldCallSubsTierApi && user?.apiKey && user?.identityToken) {
            subscriptionTiersRes = await container
                .resolve<IEntitlementService>(DI.SERVICES.IEntitlementService)
                .getAllSubscriptionTiers(
                    user.apiKey,
                    geoLocation,
                    'PC',
                    user.productKey,
                    user.identityToken,
                    '',
                    '',
                    'v2'
                );
        }
        return subscriptionTiersRes;
    };

    async amazonActivate(entitlementId: string): Promise<IAmazonActivateResponse> {
        return await this.amazonActivateUseCase.execute(entitlementId);
    }

    async bundleAndSave(payload: IBundleAndSaveParams): Promise<IBundleAndSaveResponse> {
        return await this.bundleAndSaveUseCase.execute(payload);
    }
    async bundleAndSaveInfo(payload: IBundleAndSaveInfoParams, identityToken?: string): Promise<IBundleAndSaveInfoResponse> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.bundleAndSaveInfoUseCase.execute(payload, identityToken, csrfToken);
    }
    async bundleAndSaveRules(payload: IBundleAndSaveRulesParams, identityToken?: string): Promise<IBundleAndSaveRulesResponse> {
        return await this.bundleAndSaveRulesUseCase.execute(payload, identityToken);
    }
}
